import React from "react";
import { getListQueryStatus } from "@hex-insights/app-modules";
import {
	addTimeToDate,
	Button,
	Column,
	Conditional,
	Else,
	Heading,
	If,
	makeClassName,
	Modal,
	ModalProps,
	Row,
	stringToLocalDate,
	toGrammaticalNumber,
	toLocalDateString,
} from "@hex-insights/core";
import { Icon } from "../../../../../Components/Icon";
import {
	SchoolAttendanceRecordFilterInput,
	StudentFilterInput,
	useSchoolAttendanceRecordCountStatisticsRangeQuery,
	useSchoolAttendanceRecordCountStatisticsWithStatusesQuery,
} from "../../../../../Utilities";
import { AttendanceRatingBadge } from "../../../_General";
import {
	AttendanceRecordStatisticsTable,
	SchoolAttendanceRecordDonutChart,
	SchoolAttendanceRecordDonutChartGridLegend,
	SchoolAttendanceRecordLineChart,
	statisticsToDonutChartData,
	statisticsToLineChartData,
} from "../../../_General/Attendance/Reports/Displays";
import {
	DateFormTooltipButton,
	DateRangeFormTooltipButton,
	TableChartButtonSwitch,
	useDateFormState,
	useDateRangeFormState,
} from "../../../_General/Attendance/Reports/Forms";
import { StudentsSchoolAttendanceListExportButton } from "../../StudentsListExportButton";
import styles from "./styles.module.css";

export type SchoolAttendanceRecordReportModalProps = Pick<ModalProps, "ifRef" | "onClose" | "className" | "style"> & {
	initialDate?: string;
	schoolAttendanceRecordFilters?: SchoolAttendanceRecordFilterInput;
	studentFilters?: StudentFilterInput;
};

export function SchoolAttendanceRecordReportModal({
	initialDate,
	schoolAttendanceRecordFilters,
	studentFilters,
	ifRef,
	onClose,
	className,
	style,
}: SchoolAttendanceRecordReportModalProps) {
	const [view, setView] = React.useState<"single-day" | "range">("single-day");

	return (
		<Modal ifRef={ifRef} onClose={onClose} className={makeClassName(styles["modal"], className)} style={style}>
			<Modal.Header>
				<Heading.H2 noMargin>Attendance</Heading.H2>
			</Modal.Header>
			<Modal.Body>
				<Column justify="spaced-start" verticalSpacing="1.5rem" style={{ height: "100%" }}>
					<Row justify="spaced-start" align="center">
						<Icon.AddressBook size={24} />
						<strong>Attendance Rating</strong>
						<AttendanceRatingBadge rating="good" />
					</Row>

					<Conditional>
						<If condition={view === "range"}>
							<RangeView
								initialDate={initialDate}
								schoolAttendanceRecordFilters={schoolAttendanceRecordFilters}
								studentFilters={studentFilters}
								setView={setView}
							/>
						</If>
						<Else>
							<SingleDayView
								initialDate={initialDate}
								schoolAttendanceRecordFilters={schoolAttendanceRecordFilters}
								studentFilters={studentFilters}
								setView={setView}
							/>
						</Else>
					</Conditional>
				</Column>
			</Modal.Body>
		</Modal>
	);
}

type SingleDayViewProps = {
	initialDate?: string;
	schoolAttendanceRecordFilters?: SchoolAttendanceRecordFilterInput;
	studentFilters?: StudentFilterInput;
	setView: (view: "range") => void;
};

function SingleDayView({
	initialDate,
	schoolAttendanceRecordFilters,
	studentFilters,
	setView: setParentView,
}: SingleDayViewProps) {
	const { dateFormState, date } = useDateFormState(initialDate);

	const [view, setView] = React.useState("table");

	const onClickChangeParentView = React.useCallback(() => {
		setParentView("range");
	}, [setParentView]);

	return (
		<Column justify="spaced-start" verticalSpacing="1rem" className={styles["modal__report-container"]}>
			<Column justify="spaced-start" verticalSpacing="1rem" style={{ flexGrow: 1 }}>
				<Row justify="space-between" align="center">
					<Row justify="spaced-start" align="center">
						<strong>School Attendance</strong>

						<DateFormTooltipButton formState={dateFormState} date={date} />

						<Button variant="link" size="small" onClick={onClickChangeParentView}>
							View Range
						</Button>
					</Row>

					<TableChartButtonSwitch view={view} setView={setView} />
				</Row>

				<SingleDayViewDisplay
					date={date}
					schoolAttendanceRecordFilters={schoolAttendanceRecordFilters}
					studentFilters={studentFilters}
					view={view}
				/>
			</Column>

			<StudentsSchoolAttendanceListExportButton variant="link" size="small" date={date} studentFilters={studentFilters}>
				<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
					<Icon.Export size={16} /> Export Student List
				</Row>
			</StudentsSchoolAttendanceListExportButton>
		</Column>
	);
}

type SingleDayViewDisplayProps = {
	date: string;
	schoolAttendanceRecordFilters?: SchoolAttendanceRecordFilterInput;
	studentFilters?: StudentFilterInput;
	view?: string;
};

function SingleDayViewDisplay({
	date,
	schoolAttendanceRecordFilters,
	studentFilters,
	view = "table",
}: SingleDayViewDisplayProps) {
	const { loading, data, error } = useSchoolAttendanceRecordCountStatisticsWithStatusesQuery({
		variables: {
			date,
			schoolAttendanceRecordFilters,
			studentFilters,
		},
		fetchPolicy: "cache-and-network",
	});
	const statusesConnectionData = data
		? { connection: { edges: data.schoolAttendanceRecordCountStatistics.statuses } }
		: undefined;
	const { chartData, numStudents } = React.useMemo(
		() => ({
			chartData: statisticsToDonutChartData(data?.schoolAttendanceRecordCountStatistics),
			numStudents: data?.schoolAttendanceRecordCountStatistics.students ?? 0,
		}),
		[data],
	);
	const queryStatus = getListQueryStatus({ loading, data: statusesConnectionData, error, resultKey: "connection" });

	if (view === "chart") {
		return (
			<Row justify="spaced-center" align="center" horizontalSpacing="3rem" style={{ flexGrow: 1 }}>
				<SchoolAttendanceRecordDonutChart
					data={chartData}
					centerValue={numStudents}
					centerLabel={toGrammaticalNumber("Student", numStudents)}
					size="12.5rem"
				/>
				<SchoolAttendanceRecordDonutChartGridLegend data={chartData} />
			</Row>
		);
	}

	return (
		<AttendanceRecordStatisticsTable
			date={date}
			queryStatus={queryStatus}
			statistics={data?.schoolAttendanceRecordCountStatistics}
			studentFilters={studentFilters}
		/>
	);
}

type RangeViewProps = {
	initialDate?: string;
	schoolAttendanceRecordFilters?: SchoolAttendanceRecordFilterInput;
	studentFilters?: StudentFilterInput;
	setView: (view: "single-day") => void;
};

function RangeView({
	initialDate,
	schoolAttendanceRecordFilters,
	studentFilters,
	setView: setParentView,
}: RangeViewProps) {
	const { dateFormState, startDate, endDate } = useDateRangeFormState(initialDate);

	const { data } = useSchoolAttendanceRecordCountStatisticsRangeQuery({
		variables: {
			startDate,
			endDate: toLocalDateString(addTimeToDate(stringToLocalDate(endDate, "day"), [1, "day"])),
			schoolAttendanceRecordFilters,
			studentFilters,
		},
		fetchPolicy: "cache-and-network",
	});
	const chartData = React.useMemo(
		() => statisticsToLineChartData(data?.schoolAttendanceRecordCountStatisticsRange),
		[data],
	);

	const onClickChangeParentView = React.useCallback(() => {
		setParentView("single-day");
	}, [setParentView]);

	return (
		<Column justify="spaced-start" verticalSpacing="1rem" className={styles["modal__report-container"]}>
			<Row justify="spaced-start" align="center">
				<strong>School Attendance</strong>

				<DateRangeFormTooltipButton formState={dateFormState} startDate={startDate} endDate={endDate} />

				<Button variant="link" size="small" onClick={onClickChangeParentView}>
					View Day
				</Button>
			</Row>

			<SchoolAttendanceRecordLineChart data={chartData} />
		</Column>
	);
}
